/**
 * Forecast Service
 *
 * Enhanced forecasting functions for the Reports Dashboard Forecasting tab.
 * Supports multiple time periods, historical comparisons, and customer drill-down.
 *
 * Design decisions (user selected):
 * - Data source: Historical sales + predictions overlay (Option B)
 * - Comparison: Predicted vs historical volume (Option A)
 * - Quarters: Calendar quarters Q1=Jan-Mar (Option A)
 * - Drill-down: Only customers with predictions in selected period (Option B)
 */

import { supabase } from '../supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

// =============================================================================
// TIME PERIOD UTILITIES
// =============================================================================

/**
 * Calculate date ranges for different time periods
 *
 * @param {string} period - 'week' | '2weeks' | 'month' | '3months' | 'quarter' | 'year'
 * @param {number} [offset=0] - Offset for previous periods (e.g., -1 for previous quarter)
 * @returns {Object} { startDate, endDate, label, comparisonStart, comparisonEnd }
 */
export function getDateRangeForPeriod(period, offset = 0) {
  const now = new Date()
  let startDate, endDate, label, comparisonStart, comparisonEnd

  switch (period) {
    case 'week': {
      startDate = new Date(now)
      startDate.setDate(startDate.getDate() + (offset * 7))
      endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + 6)

      // Historical comparison: same week last year
      comparisonStart = new Date(startDate)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(endDate)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)

      label = offset === 0 ? 'This Week' : offset === -1 ? 'Last Week' : `Week ${offset}`
      break
    }

    case '2weeks': {
      startDate = new Date(now)
      startDate.setDate(startDate.getDate() + (offset * 14))
      endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + 13)

      comparisonStart = new Date(startDate)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(endDate)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)

      label = offset === 0 ? 'Next 2 Weeks' : `2 Weeks (${offset})`
      break
    }

    case 'month': {
      startDate = new Date(now.getFullYear(), now.getMonth() + offset, 1)
      endDate = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0)

      comparisonStart = new Date(startDate)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(endDate)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)

      const monthName = startDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      label = offset === 0 ? 'This Month' : monthName
      break
    }

    case '3months': {
      startDate = new Date(now.getFullYear(), now.getMonth() + (offset * 3), 1)
      endDate = new Date(now.getFullYear(), now.getMonth() + (offset * 3) + 3, 0)

      comparisonStart = new Date(startDate)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(endDate)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)

      label = offset === 0 ? 'Next 3 Months' : `3 Months (${offset})`
      break
    }

    case 'quarter': {
      // Calendar quarters: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
      const currentQuarter = Math.floor(now.getMonth() / 3)
      const targetQuarter = currentQuarter + offset
      const targetYear = now.getFullYear() + Math.floor(targetQuarter / 4)
      const quarterInYear = ((targetQuarter % 4) + 4) % 4

      startDate = new Date(targetYear, quarterInYear * 3, 1)
      endDate = new Date(targetYear, quarterInYear * 3 + 3, 0)

      comparisonStart = new Date(startDate)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(endDate)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)

      const quarterLabel = `Q${quarterInYear + 1} ${targetYear}`
      label = offset === 0 ? `Current Quarter (${quarterLabel})` : quarterLabel
      break
    }

    case 'year': {
      const targetYear = now.getFullYear() + offset
      startDate = new Date(targetYear, 0, 1)
      endDate = new Date(targetYear, 11, 31)

      comparisonStart = new Date(targetYear - 1, 0, 1)
      comparisonEnd = new Date(targetYear - 1, 11, 31)

      label = offset === 0 ? `${targetYear} (Current)` : `${targetYear}`
      break
    }

    default:
      throw new Error(`Unknown period: ${period}`)
  }

  return {
    startDate: toLocalDateString(startDate),
    endDate: toLocalDateString(endDate),
    comparisonStart: toLocalDateString(comparisonStart),
    comparisonEnd: toLocalDateString(comparisonEnd),
    label
  }
}

// =============================================================================
// FORECAST DATA FETCHING
// =============================================================================

/**
 * Get forecast data for a specific time period
 * Includes both predictions and historical comparison data
 *
 * @param {string} period - Time period ('week', '2weeks', 'month', '3months', 'quarter', 'year')
 * @param {number} [offset=0] - Period offset (e.g., -1 for previous quarter)
 * @returns {Promise<Object>} Forecast data with predictions and historical comparison
 */
export async function getForecastByPeriod(period, offset = 0) {
  const { startDate, endDate, comparisonStart, comparisonEnd, label } = getDateRangeForPeriod(period, offset)

  // Fetch predictions for the period
  const { data: predictions, error: predError } = await supabase
    .from('sale_suggestions')
    .select(`
      id,
      suggested_date,
      suggested_quantity,
      confidence_score,
      product:products(id, strain),
      branch:branches(
        id,
        name,
        customer:customers(id, name),
        type:customer_types(id, name)
      )
    `)
    .eq('status', 'pending')
    .gte('suggested_date', startDate)
    .lte('suggested_date', endDate)
    .order('suggested_date', { ascending: true })

  if (predError) {
    console.error('[ForecastService] Error fetching predictions:', predError)
    throw predError
  }

  // Fetch historical sales for comparison period
  const { data: historical, error: histError } = await supabase
    .from('sales')
    .select(`
      id,
      date,
      sale_items(
        quantity,
        product:products(id, strain)
      ),
      branch:branches(
        id,
        name,
        customer:customers(id, name),
        type:customer_types(id, name)
      )
    `)
    .gte('date', comparisonStart)
    .lte('date', comparisonEnd)
    .order('date', { ascending: true })

  if (histError) {
    console.error('[ForecastService] Error fetching historical data:', histError)
    throw histError
  }

  // Aggregate predictions
  const predictionsByDate = {}
  const predictionsByProduct = {}
  const predictionsByCustomerType = {}
  const uniquePredictedDeliveries = new Set()
  const customersWithPredictions = new Set()
  let totalPredictedVolume = 0

  for (const pred of predictions || []) {
    const date = pred.suggested_date
    const strain = pred.product?.strain || 'Unknown'
    const customerType = pred.branch?.type?.name || 'Unknown'
    const customerId = pred.branch?.customer?.id
    const qty = pred.suggested_quantity || 0

    totalPredictedVolume += qty
    uniquePredictedDeliveries.add(`${pred.branch?.id}-${date}`)
    if (customerId) customersWithPredictions.add(customerId)

    // By date
    if (!predictionsByDate[date]) {
      predictionsByDate[date] = { date, predicted: 0, historical: 0 }
    }
    predictionsByDate[date].predicted += qty

    // By product
    if (!predictionsByProduct[strain]) {
      predictionsByProduct[strain] = { strain, predicted: 0, historical: 0 }
    }
    predictionsByProduct[strain].predicted += qty

    // By customer type
    if (!predictionsByCustomerType[customerType]) {
      predictionsByCustomerType[customerType] = { type: customerType, predicted: 0, historical: 0 }
    }
    predictionsByCustomerType[customerType].predicted += qty
  }

  // Aggregate historical data
  let totalHistoricalVolume = 0
  const historicalDeliveries = new Set()

  for (const sale of historical || []) {
    const date = sale.date
    historicalDeliveries.add(`${sale.branch?.id}-${date}`)

    for (const item of sale.sale_items || []) {
      const strain = item.product?.strain || 'Unknown'
      const customerType = sale.branch?.type?.name || 'Unknown'
      const qty = item.quantity || 0

      totalHistoricalVolume += qty

      // Add to comparison data
      if (predictionsByProduct[strain]) {
        predictionsByProduct[strain].historical += qty
      } else {
        predictionsByProduct[strain] = { strain, predicted: 0, historical: qty }
      }

      if (predictionsByCustomerType[customerType]) {
        predictionsByCustomerType[customerType].historical += qty
      } else {
        predictionsByCustomerType[customerType] = { type: customerType, predicted: 0, historical: qty }
      }
    }
  }

  // Calculate metrics
  const volumeChange = totalHistoricalVolume > 0
    ? ((totalPredictedVolume - totalHistoricalVolume) / totalHistoricalVolume) * 100
    : 0

  return {
    period: label,
    dateRange: { start: startDate, end: endDate },
    comparisonRange: { start: comparisonStart, end: comparisonEnd },

    // Summary metrics
    summary: {
      predictedDeliveries: uniquePredictedDeliveries.size,
      predictedVolume: totalPredictedVolume,
      historicalDeliveries: historicalDeliveries.size,
      historicalVolume: totalHistoricalVolume,
      volumeChangePercent: volumeChange,
      customersWithPredictions: customersWithPredictions.size,
      productLinesCount: predictions?.length || 0
    },

    // Breakdown data
    byDate: Object.values(predictionsByDate).sort((a, b) => a.date.localeCompare(b.date)),
    byProduct: Object.values(predictionsByProduct).sort((a, b) => b.predicted - a.predicted),
    byCustomerType: Object.values(predictionsByCustomerType).sort((a, b) => b.predicted - a.predicted),

    // Raw data for drill-down
    predictions: predictions || [],
    customerIds: Array.from(customersWithPredictions)
  }
}

/**
 * Get forecast summary for multiple periods at once
 * Used by the summary cards to show all time horizons
 *
 * @returns {Promise<Object>} Summary data for all periods
 */
export async function getForecastSummaryAllPeriods() {
  const periods = ['week', '2weeks', 'month', '3months', 'quarter', 'year']
  const results = {}

  // Fetch all periods in parallel
  const promises = periods.map(async (period) => {
    const data = await getForecastByPeriod(period, 0)
    return { period, data }
  })

  const responses = await Promise.all(promises)

  for (const { period, data } of responses) {
    results[period] = {
      label: data.period,
      predictedDeliveries: data.summary.predictedDeliveries,
      predictedVolume: data.summary.predictedVolume,
      historicalVolume: data.summary.historicalVolume,
      volumeChangePercent: data.summary.volumeChangePercent
    }
  }

  return results
}

// =============================================================================
// CUSTOMER DRILL-DOWN
// =============================================================================

/**
 * Get customers with predictions in a specific period
 * Option B: Only customers with predictions in selected period
 *
 * @param {string} period - Time period
 * @param {number} [offset=0] - Period offset
 * @returns {Promise<Array>} Customers with their prediction summaries
 */
export async function getCustomersWithPredictions(period, offset = 0) {
  const { startDate, endDate } = getDateRangeForPeriod(period, offset)

  const { data, error } = await supabase
    .from('sale_suggestions')
    .select(`
      id,
      suggested_date,
      suggested_quantity,
      confidence_score,
      product:products(id, strain),
      branch:branches(
        id,
        name,
        customer:customers(id, name),
        type:customer_types(id, name)
      )
    `)
    .eq('status', 'pending')
    .gte('suggested_date', startDate)
    .lte('suggested_date', endDate)

  if (error) {
    console.error('[ForecastService] Error fetching customers with predictions:', error)
    throw error
  }

  // Aggregate by customer
  const customerMap = {}

  for (const pred of data || []) {
    const customer = pred.branch?.customer
    if (!customer) continue

    const customerId = customer.id

    if (!customerMap[customerId]) {
      customerMap[customerId] = {
        id: customerId,
        name: customer.name,
        type: pred.branch?.type?.name || 'Unknown',
        predictions: [],
        totalVolume: 0,
        deliveryCount: 0,
        avgConfidence: 0,
        products: new Set()
      }
    }

    customerMap[customerId].predictions.push({
      id: pred.id,
      date: pred.suggested_date,
      quantity: pred.suggested_quantity,
      confidence: pred.confidence_score,
      product: pred.product?.strain
    })

    customerMap[customerId].totalVolume += pred.suggested_quantity || 0
    customerMap[customerId].avgConfidence += pred.confidence_score || 0
    if (pred.product?.strain) {
      customerMap[customerId].products.add(pred.product.strain)
    }
  }

  // Calculate averages and convert sets to arrays
  return Object.values(customerMap).map(c => {
    const deliveryDates = new Set(c.predictions.map(p => p.date))
    return {
      id: c.id,
      name: c.name,
      type: c.type,
      totalVolume: c.totalVolume,
      deliveryCount: deliveryDates.size,
      productCount: c.products.size,
      products: Array.from(c.products),
      avgConfidence: c.predictions.length > 0 ? c.avgConfidence / c.predictions.length : 0,
      predictions: c.predictions.sort((a, b) => a.date.localeCompare(b.date))
    }
  }).sort((a, b) => b.totalVolume - a.totalVolume)
}

/**
 * Get detailed forecast for a specific customer
 *
 * @param {string} customerId - Customer UUID
 * @param {string} period - Time period
 * @param {number} [offset=0] - Period offset
 * @returns {Promise<Object>} Customer forecast details with predictions and historical comparison
 */
export async function getCustomerForecast(customerId, period, offset = 0) {
  const { startDate, endDate, comparisonStart, comparisonEnd, label } = getDateRangeForPeriod(period, offset)

  // Get branches for this customer
  const { data: branches, error: branchError } = await supabase
    .from('branches')
    .select('id')
    .eq('customer_id', customerId)

  if (branchError) {
    console.error('[ForecastService] Error fetching customer branches:', branchError)
    throw branchError
  }

  const branchIds = branches?.map(b => b.id) || []

  if (branchIds.length === 0) {
    return {
      customerId,
      period: label,
      dateRange: { start: startDate, end: endDate },
      summary: { predictedVolume: 0, historicalVolume: 0, deliveryCount: 0 },
      byDate: [],
      byProduct: [],
      predictions: []
    }
  }

  // Fetch predictions for this customer's branches
  const { data: predictions, error: predError } = await supabase
    .from('sale_suggestions')
    .select(`
      id,
      suggested_date,
      suggested_quantity,
      confidence_score,
      product:products(id, strain),
      branch:branches(id, name)
    `)
    .eq('status', 'pending')
    .in('branch_id', branchIds)
    .gte('suggested_date', startDate)
    .lte('suggested_date', endDate)
    .order('suggested_date', { ascending: true })

  if (predError) {
    console.error('[ForecastService] Error fetching customer predictions:', predError)
    throw predError
  }

  // Fetch historical sales for comparison
  const { data: historical, error: histError } = await supabase
    .from('sales')
    .select(`
      id,
      date,
      sale_items(quantity, product:products(id, strain))
    `)
    .in('branch_id', branchIds)
    .gte('date', comparisonStart)
    .lte('date', comparisonEnd)

  if (histError) {
    console.error('[ForecastService] Error fetching customer historical data:', histError)
    throw histError
  }

  // Aggregate predictions
  const byDate = {}
  const byProduct = {}
  let totalPredicted = 0
  const uniqueDeliveries = new Set()

  for (const pred of predictions || []) {
    const date = pred.suggested_date
    const strain = pred.product?.strain || 'Unknown'
    const qty = pred.suggested_quantity || 0

    totalPredicted += qty
    uniqueDeliveries.add(`${pred.branch?.id}-${date}`)

    if (!byDate[date]) {
      byDate[date] = { date, predicted: 0, historical: 0 }
    }
    byDate[date].predicted += qty

    if (!byProduct[strain]) {
      byProduct[strain] = { strain, predicted: 0, historical: 0 }
    }
    byProduct[strain].predicted += qty
  }

  // Aggregate historical
  let totalHistorical = 0

  for (const sale of historical || []) {
    for (const item of sale.sale_items || []) {
      const strain = item.product?.strain || 'Unknown'
      const qty = item.quantity || 0

      totalHistorical += qty

      if (byProduct[strain]) {
        byProduct[strain].historical += qty
      } else {
        byProduct[strain] = { strain, predicted: 0, historical: qty }
      }
    }
  }

  return {
    customerId,
    period: label,
    dateRange: { start: startDate, end: endDate },
    comparisonRange: { start: comparisonStart, end: comparisonEnd },
    summary: {
      predictedVolume: totalPredicted,
      historicalVolume: totalHistorical,
      deliveryCount: uniqueDeliveries.size,
      volumeChangePercent: totalHistorical > 0
        ? ((totalPredicted - totalHistorical) / totalHistorical) * 100
        : 0
    },
    byDate: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
    byProduct: Object.values(byProduct).sort((a, b) => b.predicted - a.predicted),
    predictions: predictions || []
  }
}

// =============================================================================
// COMPARISON DATA
// =============================================================================

/**
 * Get quarter-over-quarter comparison
 * Compares current quarter predictions with previous quarter actual sales
 *
 * @param {number} [quarterOffset=0] - Quarter offset (0 = current, -1 = previous)
 * @returns {Promise<Object>} Quarter comparison data
 */
export async function getQuarterComparison(quarterOffset = 0) {
  const currentQuarter = await getForecastByPeriod('quarter', quarterOffset)
  const previousQuarter = await getForecastByPeriod('quarter', quarterOffset - 1)

  return {
    current: {
      label: currentQuarter.period,
      dateRange: currentQuarter.dateRange,
      summary: currentQuarter.summary,
      byProduct: currentQuarter.byProduct,
      byCustomerType: currentQuarter.byCustomerType
    },
    previous: {
      label: previousQuarter.period,
      dateRange: previousQuarter.dateRange,
      summary: previousQuarter.summary,
      byProduct: previousQuarter.byProduct,
      byCustomerType: previousQuarter.byCustomerType
    },
    comparison: {
      volumeChange: currentQuarter.summary.predictedVolume - previousQuarter.summary.predictedVolume,
      volumeChangePercent: previousQuarter.summary.predictedVolume > 0
        ? ((currentQuarter.summary.predictedVolume - previousQuarter.summary.predictedVolume) / previousQuarter.summary.predictedVolume) * 100
        : 0,
      deliveryChange: currentQuarter.summary.predictedDeliveries - previousQuarter.summary.predictedDeliveries
    }
  }
}

/**
 * Get year-over-year comparison
 * Compares current year predictions with previous year actual sales
 *
 * @param {number} [yearOffset=0] - Year offset (0 = current, -1 = previous)
 * @returns {Promise<Object>} Year comparison data
 */
export async function getYearComparison(yearOffset = 0) {
  const currentYear = await getForecastByPeriod('year', yearOffset)
  const previousYear = await getForecastByPeriod('year', yearOffset - 1)

  return {
    current: {
      label: currentYear.period,
      dateRange: currentYear.dateRange,
      summary: currentYear.summary,
      byProduct: currentYear.byProduct,
      byCustomerType: currentYear.byCustomerType
    },
    previous: {
      label: previousYear.period,
      dateRange: previousYear.dateRange,
      summary: previousYear.summary,
      byProduct: previousYear.byProduct,
      byCustomerType: previousYear.byCustomerType
    },
    comparison: {
      volumeChange: currentYear.summary.predictedVolume - previousYear.summary.predictedVolume,
      volumeChangePercent: previousYear.summary.predictedVolume > 0
        ? ((currentYear.summary.predictedVolume - previousYear.summary.predictedVolume) / previousYear.summary.predictedVolume) * 100
        : 0,
      deliveryChange: currentYear.summary.predictedDeliveries - previousYear.summary.predictedDeliveries
    }
  }
}
